import { FunctionComponent } from 'react';
import { Dialog, makeStyles } from '@material-ui/core';
import { CustomizedButton, CustomizedTypography } from '@components/Customized';
import {
  CapaErrorCode,
  CapaQuotationChatMessageType,
  CapaQuotationChatRoomStatus,
  useFetchQuotationChatRoomLazyQuery,
  useFetchQuotationPartnerQuery,
  useSendSystemChatMessageMutation
} from '@generated/graphql';
import { useHistory } from 'react-router-dom';
import useQueryString from '@hooks/useQueryString';
import { useSnackbar } from 'notistack';

interface NotFoundPartnerSubmallProps {
  partnerId?: string;
  chatRoomId?: string;
  onCloseAtDashboard?: () => void;
}

const NotFoundPartnerSubmall: FunctionComponent<NotFoundPartnerSubmallProps> = ({
  partnerId,
  chatRoomId,
  onCloseAtDashboard
}) => {
  const classes = useStyles();
  const history = useHistory();
  const [
    sendSubmallRegisterRequestChatMessage
  ] = useSendSystemChatMessageMutation();
  const {
    query: { e },
    onUpdateQueryString
  } = useQueryString<{ p?: string; e?: string }>();
  const { enqueueSnackbar } = useSnackbar();

  useFetchQuotationPartnerQuery({
    variables: {
      quotationPartnerId: partnerId ?? ''
    },
    skip: !partnerId || e === '2',
    fetchPolicy: 'no-cache',
    onCompleted: (result) => {
      if (result.fetchQuotationPartner?.result === CapaErrorCode.Success) {
        if (!result?.fetchQuotationPartner?.data?.haveSubmall) {
          onUpdateQueryString({ p: '2', e: '2' });
        }
      }
    }
  });

  const [fetchChatRoom] = useFetchQuotationChatRoomLazyQuery({
    variables: {
      input: {
        chatRoomId: chatRoomId as string
      }
    }
  });

  const onClickClose = async () => {
    const { pathname } = window.location;

    if (chatRoomId) {
      try {
        const chatRoomData = await fetchChatRoom();

        const isChatRoomOpened =
          chatRoomData?.data?.fetchQuotationChatRoom?.data?.chatRoomStatus ===
          CapaQuotationChatRoomStatus.Opened;

        chatRoomId &&
          isChatRoomOpened &&
          sendSubmallRegisterRequestChatMessage({
            variables: {
              input: {
                chatRoomId: chatRoomId,
                messageType:
                  CapaQuotationChatMessageType.SubmallRegistrationRequest
              }
            }
          });
      } catch (e) {
        enqueueSnackbar('채팅방 정보를 불러오지 못했습니다.');
      }
    }

    if (onCloseAtDashboard) {
      return onCloseAtDashboard();
    }

    history.push(pathname);
  };

  return (
    <Dialog fullWidth open={e === '2'} classes={{ paper: classes.paper }}>
      <div className={classes.header}>
        <CustomizedTypography variant="h4" fontWeight="bold" color="error">
          파트너 정산계좌 미등록
        </CustomizedTypography>
      </div>
      <div className={classes.content}>
        <CustomizedTypography variant="body2" color="textPrimary">
          파트너가 입금 받을 계좌를 등록하지 않았습니다. <br />
          등록이 완료되면 정상적으로 거래가 진행됩니다. 잠시 뒤 다시
          시도해주세요.
        </CustomizedTypography>
      </div>
      <div className={classes.footer}>
        <CustomizedButton
          fullWidth
          variant="contained"
          color="default"
          onClick={onClickClose}
          size="large"
        >
          닫기
        </CustomizedButton>
      </div>
    </Dialog>
  );
};

export default NotFoundPartnerSubmall;

const useStyles = makeStyles((theme) => ({
  paper: {
    maxWidth: 476,
    padding: 0,
    borderRadius: 16,
    '& h4': {
      wordBreak: 'break-all'
    }
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    [theme.breakpoints.down('md')]: {
      padding: '28px 20px'
    },
    '& .close': {
      padding: 0,
      '& svg': {
        width: 24,
        height: 24
      }
    }
  },
  content: {
    padding: '0px 24px 24px',
    [theme.breakpoints.down('md')]: {
      padding: '0px 16px 24px'
    }
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 24px',
    [theme.breakpoints.down('md')]: {
      padding: '16px'
    }
  }
}));
